import type { DataScope, UserEnd } from '../enums';
import { EXTERNAL_ENDS } from '../enums';

/**
 * RBAC 落地：菜单级（路由 meta.perm）+ 按钮级（v-perm / hasPerm）+ 数据范围 + 端绑定互斥。
 * 前端仅控制展示与交互可达性；行级过滤与操作校验由后端强制（前端不作为安全边界）。
 */

export interface PermissionContext {
  /** 按钮/菜单权限码集合，如 order:assign */
  permissions: string[];
  dataScope: DataScope;
  endBinding: UserEnd[];
  roleCode: string;
  builtInSuperAdmin?: boolean;
}

let resolver: () => PermissionContext | null = () => null;

export function configurePermissionContext(fn: () => PermissionContext | null): void {
  resolver = fn;
}

export function permissionContext(): PermissionContext | null {
  return resolver();
}

/** 超级管理员通配（不可删角色，拥有全部权限） */
const SUPER_ADMIN_CODE = 'SUPER_ADMIN';

export function hasPerm(code?: string | null): boolean {
  if (!code) return true;
  const ctx = resolver();
  if (!ctx) return false;
  if (ctx.builtInSuperAdmin || ctx.roleCode === SUPER_ADMIN_CODE) return true;
  if (ctx.permissions.includes('*')) return true;
  return ctx.permissions.includes(code);
}

export function hasAnyPerm(codes: string[]): boolean {
  return codes.some((code) => hasPerm(code));
}

export function hasAllPerms(codes: string[]): boolean {
  return codes.every((code) => hasPerm(code));
}

/** 端绑定互斥：外部角色（BPO客服/交付专员）仅绑专属端，禁止开通管理端 */
export function isEndAllowed(currentEnd: UserEnd, endBinding: UserEnd[]): boolean {
  if (!endBinding || endBinding.length === 0) return false;
  return endBinding.includes(currentEnd);
}

export function isExternalEnd(end: UserEnd): boolean {
  return EXTERNAL_ENDS.includes(end);
}

/** 角色可选端：外部角色不可勾管理端（表单禁用 + tooltip 说明） */
export function allowedEndsFor(isExternal: boolean): UserEnd[] {
  return isExternal ? [...EXTERNAL_ENDS] : ['ADMIN'];
}

/** 聚合权限树三态：全选 / 半选 / 未选 */
export type TriState = 'CHECKED' | 'INDETERMINATE' | 'UNCHECKED';

export interface PermTreeNode {
  code: string;
  children?: PermTreeNode[];
}

export function triStateOf(node: PermTreeNode, granted: Set<string>): TriState {
  const leaves = collectLeafCodes(node);
  if (leaves.length === 0) return granted.has(node.code) ? 'CHECKED' : 'UNCHECKED';
  const hit = leaves.filter((code) => granted.has(code)).length;
  if (hit === 0) return granted.has(node.code) ? 'CHECKED' : 'UNCHECKED';
  if (hit === leaves.length) return 'CHECKED';
  return 'INDETERMINATE';
}

export function collectLeafCodes(node: PermTreeNode): string[] {
  if (!node.children || node.children.length === 0) return [node.code];
  return node.children.flatMap((child) => collectLeafCodes(child));
}
